import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { useI18n } from "@/lib/I18nContext";
import { pickText, toBilingual } from "@/lib/bilingual";
import BilingualEditor from "@/components/BilingualEditor";
import { toast } from "sonner";
import { Plus, Trash2, ImagePlus, X, ArrowRight, Edit3, Save } from "lucide-react";

const PLACEHOLDER_IMG =
  "https://images.pexels.com/photos/34649361/pexels-photo-34649361.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

function resolveImage(url) {
  if (!url) return PLACEHOLDER_IMG;
  if (url.startsWith("http")) return url;
  const base = API_BASE.replace(/\/api$/, "");
  return `${base}${url}`;
}

export default function Noticias() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [news, setNews] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [title, setTitle] = useState({ es: "", en: "" });
  const [body, setBody] = useState({ es: "", en: "" });
  const [imageUrl, setImageUrl] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    const r = await api.get("/news");
    setNews(r.data);
  };

  useEffect(() => {
    load();
  }, []);

  // SEO: update page title + meta description for crawlers
  useEffect(() => {
    document.title = "Noticias del Mundial 2026 | La Polla Mundialista";
    let m = document.querySelector('meta[name="description"]');
    if (!m) {
      m = document.createElement("meta");
      m.setAttribute("name", "description");
      document.head.appendChild(m);
    }
    const summary = news.slice(0, 3).map((n) => pickText(n.title, lang)).filter(Boolean).join(" · ");
    m.setAttribute(
      "content",
      summary
        ? `Últimas noticias del FIFA World Cup 2026: ${summary} — La Polla Mundialista.`
        : "Sigue las últimas noticias del FIFA World Cup 2026 en La Polla Mundialista."
    );
    return () => {
      document.title = "La Polla Mundialista";
    };
  }, [news, lang]);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error(t("news.pickImage"));
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      toast.error(t("news.imageTooLarge"));
      return;
    }
    setImageFile(f);
    setImagePreview(URL.createObjectURL(f));
  };

  const clearImage = () => {
    setImageFile(null);
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const resetForm = () => {
    setTitle({ es: "", en: "" });
    setBody({ es: "", en: "" });
    setImageUrl("");
    clearImage();
    setShowForm(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!(title.es || "").trim()) {
      toast.error("El título en español es obligatorio");
      return;
    }
    if (!(body.es || "").trim()) {
      toast.error("El contenido en español es obligatorio");
      return;
    }
    try {
      setUploading(true);
      let finalImageUrl = imageUrl?.trim() || null;
      if (imageFile) {
        const fd = new FormData();
        fd.append("file", imageFile);
        const up = await api.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        finalImageUrl = up.data?.url || finalImageUrl;
      }
      await api.post("/news", { title, body, image_url: finalImageUrl });
      resetForm();
      toast.success(t("news.published"));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("news.publishError"));
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!confirm(t("news.deleteConfirm"))) return;
    await api.delete(`/news/${id}`);
    load();
  };

  const dateLocale = lang === "en" ? "en-US" : "es-MX";

  return (
    <div className="space-y-6" data-testid="noticias-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono-data mb-2">
            {t("news.newsroom")}
          </div>
          <h1 className="font-display text-5xl md:text-6xl uppercase leading-none">{t("news.title")}</h1>
        </div>
        {user?.is_admin && (
          <button
            onClick={() => setShowForm(!showForm)}
            data-testid="toggle-news-form"
            className="bg-[var(--c-lime)] text-black font-bold uppercase tracking-wider px-4 py-2 text-xs flex items-center gap-2"
          >
            <Plus size={14} /> {showForm ? t("news.cancel") : t("news.new")}
          </button>
        )}
      </div>

      {showForm && user?.is_admin && (
        <form
          onSubmit={submit}
          className="bg-zinc-900 border border-zinc-800 p-6 space-y-4"
          data-testid="news-form"
        >
          <div className="space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data">
              {t("news.titlePlaceholder")}
            </div>
            <BilingualEditor
              value={title}
              onChange={setTitle}
              placeholderEs="Título en español"
              placeholderEn="Title in English (opcional)"
              testidPrefix="news-title"
            />
          </div>

          {/* Image: upload OR external URL fallback */}
          <div className="space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data">
              {t("news.imageLabel")}
            </div>

            <div className="flex flex-wrap gap-3 items-start">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={onPickFile}
                data-testid="news-image-file"
                className="hidden"
                id="news-image-file-input"
              />
              <label
                htmlFor="news-image-file-input"
                data-testid="news-image-file-trigger"
                className="cursor-pointer inline-flex items-center gap-2 border border-dashed border-zinc-700 hover:border-[var(--c-lime)] hover:text-[var(--c-lime)] px-3 py-2 text-xs uppercase tracking-wider text-zinc-300"
              >
                <ImagePlus size={14} />
                {imageFile ? t("news.changeImage") : t("news.uploadImage")}
              </label>

              {imagePreview && (
                <div className="relative">
                  <img
                    src={imagePreview}
                    alt="preview"
                    className="h-20 w-32 object-cover border border-zinc-800"
                    data-testid="news-image-preview"
                  />
                  <button
                    type="button"
                    onClick={clearImage}
                    data-testid="news-image-clear"
                    className="absolute -top-2 -right-2 bg-black border border-zinc-700 text-white p-1 rounded-full hover:border-[var(--c-red)] hover:text-[var(--c-red)]"
                    aria-label={t("news.removeAria")}
                  >
                    <X size={12} />
                  </button>
                </div>
              )}
            </div>

            {!imageFile && (
              <input
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder={t("news.urlPlaceholder")}
                data-testid="news-image-url-input"
                className="w-full bg-black border border-zinc-800 px-3 py-2 outline-none focus:border-[var(--c-lime)] rounded-sm text-sm"
              />
            )}
          </div>

          <div className="space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data">
              {t("news.bodyPlaceholder")}
            </div>
            <BilingualEditor
              value={body}
              onChange={setBody}
              multiline
              rows={6}
              placeholderEs="Contenido en español"
              placeholderEn="Content in English (opcional)"
              testidPrefix="news-body"
            />
          </div>

          <button
            type="submit"
            disabled={uploading}
            data-testid="news-submit-button"
            className="bg-[var(--c-lime)] text-black font-bold uppercase tracking-wider px-4 py-2 text-xs disabled:opacity-60"
          >
            {uploading ? t("news.publishing") : t("news.publish")}
          </button>
        </form>
      )}

      {news.length === 0 ? (
        <div className="bg-zinc-900 border border-zinc-800 p-12 text-center text-zinc-500 text-sm">
          {t("news.empty")}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {news.map((n) => {
            const displayTitle = pickText(n.title, lang);
            const displayBody = pickText(n.body, lang);
            return (
              <article
                key={n.id}
                className="bg-zinc-900 border border-zinc-800 overflow-hidden card-hover flex flex-col"
                data-testid={`news-card-${n.id}`}
              >
                <Link to={`/noticias/${n.id}`} className="block aspect-[16/9] overflow-hidden relative">
                  <img
                    src={resolveImage(n.image_url)}
                    alt={displayTitle}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
                </Link>
                <div className="p-5 flex flex-col flex-1">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data mb-2">
                    {new Date(n.created_at).toLocaleDateString(dateLocale, {
                      year: "numeric",
                      month: "short",
                      day: "2-digit",
                    })}
                    {n.author_nickname && <> · {n.author_nickname}</>}
                  </div>
                  <h2 className="font-display text-xl uppercase leading-tight mb-2">
                    <Link
                      to={`/noticias/${n.id}`}
                      data-testid={`news-title-link-${n.id}`}
                      className="hover:text-[var(--c-lime)] transition-colors"
                    >
                      {displayTitle}
                    </Link>
                  </h2>
                  <p className="text-zinc-400 text-sm whitespace-pre-line line-clamp-4">{displayBody}</p>

                  <div className="mt-4 flex items-center justify-between gap-3">
                    <Link
                      to={`/noticias/${n.id}`}
                      data-testid={`read-more-${n.id}`}
                      className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-[var(--c-lime)] hover:underline"
                    >
                      {t("news.readMore")} <ArrowRight size={12} />
                    </Link>
                    {user?.is_admin && (
                      <div className="flex gap-3 items-center">
                        <button
                          onClick={() => setEditingId(n.id)}
                          data-testid={`edit-news-${n.id}`}
                          className="text-xs text-[var(--c-lime)] flex items-center gap-1 hover:underline"
                        >
                          <Edit3 size={12} /> {t("news.edit")}
                        </button>
                        <button
                          onClick={() => remove(n.id)}
                          data-testid={`delete-news-${n.id}`}
                          className="text-xs text-[var(--c-red)] flex items-center gap-1 hover:underline"
                        >
                          <Trash2 size={12} /> {t("news.delete")}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {editingId && (
        <EditNewsModal
          news={news.find((n) => n.id === editingId)}
          onClose={() => setEditingId(null)}
          onSaved={async () => { setEditingId(null); await load(); }}
        />
      )}
    </div>
  );
}

function EditNewsModal({ news, onClose, onSaved }) {
  const { t } = useI18n();
  const [title, setTitle] = useState(toBilingual(news?.title));
  const [body, setBody] = useState(toBilingual(news?.body));
  const [imageUrl, setImageUrl] = useState(news?.image_url || "");
  const [newImageFile, setNewImageFile] = useState(null);
  const [newPreview, setNewPreview] = useState("");
  const [saving, setSaving] = useState(false);
  if (!news) return null;

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error(t("news.pickImageShort")); return; }
    if (f.size > 8 * 1024 * 1024) { toast.error(t("news.imageMax")); return; }
    setNewImageFile(f);
    if (newPreview) URL.revokeObjectURL(newPreview);
    setNewPreview(URL.createObjectURL(f));
  };

  const save = async () => {
    if (!(title.es || "").trim()) { toast.error("El título en español es obligatorio"); return; }
    if (!(body.es || "").trim())  { toast.error("El contenido en español es obligatorio"); return; }
    setSaving(true);
    try {
      let finalImage = imageUrl?.trim() || null;
      if (newImageFile) {
        const fd = new FormData();
        fd.append("file", newImageFile);
        const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        finalImage = up.data?.url;
      }
      await api.put(`/news/${news.id}`, {
        title: { es: title.es.trim(), en: (title.en || "").trim() },
        body:  { es: body.es.trim(),  en: (body.en  || "").trim() },
        image_url: finalImage,
      });
      toast.success(t("news.saved"));
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("news.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const resolveImg = (u) => {
    if (!u) return null;
    if (u.startsWith("http")) return u;
    return `${API_BASE.replace(/\/api$/, "")}${u}`;
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start md:items-center justify-center p-4 overflow-y-auto" data-testid="news-edit-modal">
      <div className="bg-zinc-900 border border-zinc-800 w-full max-w-3xl p-6 space-y-4 my-8">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-2xl uppercase">{t("news.editModalTitle")}</h3>
          <button onClick={onClose} className="p-2 border border-zinc-800 hover:border-white"><X size={14} /></button>
        </div>

        <div className="space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data">{t("news.titlePlaceholder")}</div>
          <BilingualEditor value={title} onChange={setTitle} testidPrefix="edit-news-title" />
        </div>

        <div className="space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data">{t("news.currentImage")}</div>
          <div className="flex gap-3 items-start flex-wrap">
            <img src={resolveImg(newPreview || imageUrl) || PLACEHOLDER_IMG} alt="" className="h-24 w-40 object-cover border border-zinc-800" />
            <div className="flex-1 min-w-[180px] space-y-2">
              <label className="cursor-pointer inline-flex items-center gap-2 border border-dashed border-zinc-700 hover:border-[var(--c-lime)] hover:text-[var(--c-lime)] px-3 py-2 text-xs uppercase tracking-wider text-zinc-300">
                <ImagePlus size={14} /> {t("news.uploadNew")}
                <input type="file" accept="image/*" className="hidden" data-testid="edit-news-file" onChange={onPickFile} />
              </label>
              <input
                value={imageUrl}
                onChange={(e) => { setImageUrl(e.target.value); setNewImageFile(null); setNewPreview(""); }}
                placeholder={t("news.urlPlaceholderShort")}
                data-testid="edit-news-image-url"
                className="w-full bg-black border border-zinc-800 px-2 py-1.5 outline-none focus:border-[var(--c-lime)] text-xs rounded-sm"
              />
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data">{t("news.bodyPlaceholder")}</div>
          <BilingualEditor value={body} onChange={setBody} multiline rows={8} testidPrefix="edit-news-body" />
        </div>

        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="border border-zinc-800 px-4 py-2 text-xs uppercase tracking-wider">{t("common.cancel")}</button>
          <button onClick={save} disabled={saving} data-testid="edit-news-save"
            className="bg-[var(--c-lime)] text-black font-bold uppercase tracking-wider px-4 py-2 text-xs disabled:opacity-50 flex items-center gap-1">
            <Save size={12} /> {saving ? t("news.saving") : t("news.saveChanges")}
          </button>
        </div>
      </div>
    </div>
  );
}
